import React from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Backdrop,
  Fade,
  Divider,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

/**
 * AccountStatusModal Component
 * Displays an account status warning with appropriate actions based on role.
 *
 * Props:
 * - open: Boolean - controls modal visibility.
 * - isAdmin: Boolean - determines if the user has admin or billing permissions.
 */
const AccountStatusModal = ({ open, isAdmin }) => {
  const navigate = useNavigate();
  const theme = useTheme(); // Access MUI theme for colors

  /**
   * Handles navigation to the billing settings page.
   */
  const handleNavigateToBilling = () => {
    navigate("/settings/billing-settings");
  };

  return (
    <Modal
      open={open}
      onClose={null} // ❌ Prevents clicking outside to close
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" }, // Softer dark overlay
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 10,
            width: 480,
            maxWidth: "90%",
            outline: "none",
          }}
          onClick={(e) => e.stopPropagation()} // ✅ Prevents accidental modal closure from outside clicks
        >
          {/* Dynamic Title Based on User Role */}
          <Typography variant="h5" fontWeight="bold" gutterBottom align="center">
            {isAdmin ? "Billing Issue Detected" : "Access Restricted"}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Dynamic Message Based on User Role */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            {isAdmin
              ? "Your account has a billing issue that requires immediate attention. Please update your billing information."
              : "Your access is restricted due to a billing issue. Contact an administrator for assistance."}
          </Typography>

          {/* Buttons - Right-Aligned */}
          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
            <Button
              onClick={handleNavigateToBilling}
              variant="contained"
              sx={{
                minWidth: 160,
                backgroundColor: theme.palette.primary.main,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Update Billing
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AccountStatusModal;
